import { Search } from 'react-feather';
import { useHistory } from 'react-router-dom';
import { useModal } from 'react-modal-hook';
import { useTranslation } from 'react-i18next';
import React, { ChangeEvent, useEffect, useState } from 'react';

import { AddInvitationMutation as AddInvitation } from '__generated__/graphql';
import { isEmpty } from '../../../../common/helpers';
import AddInvitationForm from './AddInvitationForm';
import GetInvitationQuery from './GetInvitationQuery';
import GetInvitationsQuery from './GetInvitationsQuery';
import InvitationList from './InvitationList';
import UI from '../../../../common/UI';
import useProject from '../../../useProject';
import useQuery from '../../../../api/useQuery';
import useUrlState from '../../../../common/useUrlState';

type AddedInvitation = AddInvitation['addInvitation'];

interface InvitationsPageInterface {
  eventId: string;
  invitationId: string;
}

interface SearchPaginationParams {
  search: string | null;
}

const InvitationsPage = ({ eventId, invitationId }: InvitationsPageInterface) => {
  const { t } = useTranslation();
  const history = useHistory();
  const project = useProject();

  const [filtersKey, setFiltersKey] = useState(0);

  const [urlState, setUrlState] = useUrlState<SearchPaginationParams>({
    parse: (rawParams, { string }) => ({
      search: string(rawParams.search),
    }),
    onChange: () => {
      // Rerender the filters when user clicks on 'Registrations' in the nav bar.
      setFiltersKey((key) => key + 1);
    },
  });

  const { data, refetch, loading } = useQuery(
    GetInvitationsQuery,
    {
      variables: {
        ...urlState,
        project_id: project.id,
        event_id: eventId,
      },
    },
  );

  const event = data?.project.event;

  const { data: invitationData, loading: invitationLoading } = useQuery(
    GetInvitationQuery,
    {
      variables: {
        event_id: eventId,
        invitation_id: invitationId,
      },
      skip: !invitationId,
      nextFetchPolicy: 'network-only',
    },
  );

  const [openAddInvitationModal, closeAddInvitationModal] = useModal(() => {
    const handleSuccess = (invitation: AddedInvitation) => {
      closeAddInvitationModal();
      history.push(`/${project.id}/events/${event.id}/invitation-codes/${invitation.id}`);
    };

    return (
      <UI.Modal maxWidth={600} close={closeAddInvitationModal}>
        <AddInvitationForm
          onSuccess={handleSuccess}
          event={event}
        />
      </UI.Modal>
    );
  }, [event]);

  useEffect(() => {
    refetch();
  }, [invitationId, refetch]);

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    // Search input is debounced so we can update params immediately
    setUrlState((oldParams) => ({ ...oldParams, search: event.target.value || null }), true);
  };

  // Filter helpers
  const hasFilters = Object.values(urlState).some((param) => !isEmpty(param));

  const resetFilters = () => {
    setUrlState({ search: null });
  };

  // Either we have invitations, or we have no invitations when filters are enabled
  const hasInvitations = event?.invitations.length > 0 || (event?.invitations.length === 0 && hasFilters);

  if (!event) {
    return <UI.PageLoader />;
  }

  return (
    <UI.Section>
      <UI.GridContainer>
        <UI.FlexContainer sc={{ justifyContent: 'space-between', direction: ['column', 'row'] }}>
          <UI.SectionHeading>
            {t('invitation_codes')}
          </UI.SectionHeading>

          {hasInvitations && (
            <UI.FlexContainer sc={{ spaceX: 1 }}>
              <UI.Button
                sc={{ brand: 'secondary' }}
                onClick={() => openAddInvitationModal()}
              >
                {t('add_code')}
              </UI.Button>
            </UI.FlexContainer>
          )}
        </UI.FlexContainer>

        <UI.GridContainer sc={{ columns: ['1fr', '1fr 3fr'] }}>
          <UI.Div>
            <FilterBar
              onSearch={handleSearch}
              search={urlState.search}
              key={filtersKey}
            />
          </UI.Div>

          <UI.Div>
            {!hasInvitations && (
              <UI.Card sc={{ padding: 8, textAlign: 'center', background: 'gray.50' }}>
                <UI.H3 sc={{ mb: 2 }}>{t('invite_participants')}</UI.H3>
                <UI.Paragraph>{t('invitations_get_started')}</UI.Paragraph>
                <UI.Button
                  onClick={() => openAddInvitationModal()}
                  sc={{ brand: 'secondary' }}
                >
                  {t('add_an_invitation_code')}
                </UI.Button>
              </UI.Card>
            )}
            {hasInvitations && (
              <UI.Div sc={{ loading }}>
                <InvitationList
                  event={event}
                  onResetFilters={() => resetFilters()}
                  activeInvitationId={invitationId}
                  // Always pass a fresh data object
                  invitationData={invitationData?.invitation.id === invitationId && !invitationLoading
                    ? invitationData
                    : null}
                />
              </UI.Div>
            )}
          </UI.Div>
        </UI.GridContainer>
      </UI.GridContainer>
    </UI.Section>
  );
};

interface FilterBarProps {
  search: string;
  onSearch: (event: ChangeEvent<HTMLInputElement>) => void;
}

const FilterBar = ({ search, onSearch }: FilterBarProps) => {
  const { t } = useTranslation();

  return (
    <UI.InputGroup sc={{ mb: 0 }}>
      <UI.InputWrapper>
        <span>
          <UI.Icon>
            <Search />
          </UI.Icon>
        </span>
        <UI.DebouncedInput
          type="text"
          value={search}
          onChange={onSearch}
          placeholder={t('search_invitation_codes')}
          sc={{ pl: 5 }}
        />
      </UI.InputWrapper>
    </UI.InputGroup>
  );
};

export default InvitationsPage;
